import React, { useState, useEffect, useRef } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Checkbox } from "primereact/checkbox";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { Tag } from "primereact/tag";
import { adminApi } from "../../../api/adminApi";

export default function PanelRoles() {
  const toast = useRef(null);
  const [roles, setRoles] = useState([]);
  const [catalogo, setCatalogo] = useState([]);
  const [grupos, setGrupos] = useState([]);
  const [rolSeleccionado, setRolSeleccionado] = useState(null);
  const [permisosEditados, setPermisosEditados] = useState([]);
  const [guardando, setGuardando] = useState(false);
  const [modalNuevoRol, setModalNuevoRol] = useState(false);
  const [nuevoRol, setNuevoRol] = useState({ id: "", nombre: "", descripcion: "" });
  const [creando, setCreando] = useState(false);

  useEffect(() => {
    cargarDatos();
  }, []);

  async function cargarDatos() {
    try {
      const [rolesRes, catRes] = await Promise.all([
        adminApi.getRoles(),
        adminApi.getPermisosCatalogo(),
      ]);
      setRoles(rolesRes.data);
      setCatalogo(catRes.data);
      // Agrupar permisos por grupo
      const agrupados = catRes.data.reduce((acc, p) => {
        if (!acc[p.grupo]) acc[p.grupo] = [];
        acc[p.grupo].push(p);
        return acc;
      }, {});
      setGrupos(Object.entries(agrupados));
    } catch (e) {
      toast.current?.show({ severity: "error", summary: "Error", detail: "No se pudieron cargar los roles" });
    }
  }

  function seleccionarRol(rol) {
    setRolSeleccionado(rol);
    setPermisosEditados([...rol.permisos]);
  }

  function togglePermiso(permisoId) {
    setPermisosEditados((prev) =>
      prev.includes(permisoId) ? prev.filter((p) => p !== permisoId) : [...prev, permisoId]
    );
  }

  function toggleGrupo(permisosDelGrupo) {
    const ids = permisosDelGrupo.map((p) => p.id);
    const todosActivos = ids.every((id) => permisosEditados.includes(id));
    if (todosActivos) {
      setPermisosEditados((prev) => prev.filter((p) => !ids.includes(p)));
    } else {
      setPermisosEditados((prev) => [...new Set([...prev, ...ids])]);
    }
  }

  async function guardarPermisos() {
    if (!rolSeleccionado) return;
    setGuardando(true);
    try {
      await adminApi.editarRol(rolSeleccionado.id, { permisos: permisosEditados });
      setRoles((prev) =>
        prev.map((r) =>
          r.id === rolSeleccionado.id ? { ...r, permisos: permisosEditados } : r
        )
      );
      setRolSeleccionado((prev) => ({ ...prev, permisos: permisosEditados }));
      toast.current?.show({ severity: "success", summary: "Guardado", detail: `Permisos de "${rolSeleccionado.nombre}" actualizados` });
    } catch (e) {
      toast.current?.show({ severity: "error", summary: "Error", detail: "No se pudieron guardar los permisos" });
    } finally {
      setGuardando(false);
    }
  }

  async function crearRol() {
    if (!nuevoRol.id || !nuevoRol.nombre) {
      toast.current?.show({ severity: "warn", summary: "Campos requeridos", detail: "ID y nombre son obligatorios" });
      return;
    }
    setCreando(true);
    try {
      const res = await adminApi.crearRol({ ...nuevoRol, permisos: [] });
      setRoles((prev) => [...prev, res.data]);
      setModalNuevoRol(false);
      setNuevoRol({ id: "", nombre: "", descripcion: "" });
      toast.current?.show({ severity: "success", summary: "Rol creado", detail: `Rol "${nuevoRol.nombre}" creado` });
    } catch (e) {
      const msg = e.response?.data?.error || "Error al crear el rol";
      toast.current?.show({ severity: "error", summary: "Error", detail: msg });
    } finally {
      setCreando(false);
    }
  }

  function confirmarEliminar(rol) {
    confirmDialog({
      message: `¿Eliminar el rol "${rol.nombre}"? Esta acción no se puede deshacer.`,
      header: "Eliminar rol",
      icon: "pi pi-trash",
      acceptClassName: "p-button-danger",
      acceptLabel: "Eliminar",
      rejectLabel: "Cancelar",
      accept: () => eliminarRol(rol),
    });
  }

  async function eliminarRol(rol) {
    try {
      await adminApi.eliminarRol(rol.id);
      setRoles((prev) => prev.filter((r) => r.id !== rol.id));
      if (rolSeleccionado?.id === rol.id) setRolSeleccionado(null);
      toast.current?.show({ severity: "success", summary: "Eliminado", detail: `Rol "${rol.nombre}" eliminado` });
    } catch (e) {
      const msg = e.response?.data?.error || "No se pudo eliminar el rol";
      toast.current?.show({ severity: "error", summary: "Error", detail: msg });
    }
  }

  const hayModificaciones = rolSeleccionado &&
    JSON.stringify([...permisosEditados].sort()) !== JSON.stringify([...rolSeleccionado.permisos].sort());

  return (
    <div className="p-3">
      <Toast ref={toast} />
      <ConfirmDialog />

      <div className="flex align-items-center justify-content-between mb-3">
        <h2 className="m-0">Roles y Permisos</h2>
        <Button
          label="Nuevo rol"
          icon="pi pi-plus"
          onClick={() => setModalNuevoRol(true)}
          size="small"
        />
      </div>

      <div className="grid">
        {/* Lista de roles */}
        <div className="col-12 md:col-4">
          <div className="surface-card border-round border-1 surface-border p-2">
            <p className="text-sm text-500 mb-2 px-2">Seleccioná un rol para editar sus permisos</p>
            {roles.map((rol) => (
              <div
                key={rol.id}
                className={`flex align-items-center justify-content-between p-2 border-round cursor-pointer mb-1 ${rolSeleccionado?.id === rol.id ? "bg-primary text-white" : "hover:surface-hover"}`}
                onClick={() => seleccionarRol(rol)}
              >
                <div>
                  <div className="font-semibold">{rol.nombre}</div>
                  <div className={`text-xs ${rolSeleccionado?.id === rol.id ? "text-white-alpha-70" : "text-500"}`}>
                    {rol.permisos?.length || 0} permisos
                  </div>
                </div>
                <div className="flex gap-1">
                  {rol.esRolDelSistema && <Tag value="sistema" severity="secondary" style={{ fontSize: "10px" }} />}
                  {!rol.esRolDelSistema && (
                    <Button
                      icon="pi pi-trash"
                      rounded
                      text
                      severity="danger"
                      size="small"
                      onClick={(e) => { e.stopPropagation(); confirmarEliminar(rol); }}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Matriz de permisos */}
        <div className="col-12 md:col-8">
          {!rolSeleccionado ? (
            <div className="surface-card border-round border-1 surface-border p-4 text-center text-500">
              <i className="pi pi-arrow-left text-3xl mb-2" />
              <p>Seleccioná un rol para ver y editar sus permisos</p>
            </div>
          ) : (
            <div className="surface-card border-round border-1 surface-border p-3">
              <div className="flex align-items-center justify-content-between mb-3">
                <div>
                  <h3 className="m-0">{rolSeleccionado.nombre}</h3>
                  <p className="text-sm text-500 m-0">{rolSeleccionado.descripcion}</p>
                </div>
                <Button
                  label="Guardar cambios"
                  icon="pi pi-save"
                  loading={guardando}
                  disabled={!hayModificaciones}
                  onClick={guardarPermisos}
                  size="small"
                />
              </div>

              {grupos.map(([grupo, permisos]) => {
                const todosActivos = permisos.every((p) => permisosEditados.includes(p.id));
                const algunoActivo = permisos.some((p) => permisosEditados.includes(p.id));
                return (
                  <div key={grupo} className="mb-3">
                    <div
                      className="flex align-items-center gap-2 mb-1 cursor-pointer"
                      onClick={() => toggleGrupo(permisos)}
                    >
                      <Checkbox
                        checked={todosActivos}
                        indeterminate={algunoActivo && !todosActivos}
                        onChange={() => toggleGrupo(permisos)}
                      />
                      <span className="font-bold text-sm text-600 uppercase tracking-wide">{grupo}</span>
                    </div>
                    <div className="ml-4">
                      {permisos.map((p) => (
                        <div key={p.id} className="flex align-items-center gap-2 py-1">
                          <Checkbox
                            checked={permisosEditados.includes(p.id)}
                            onChange={() => togglePermiso(p.id)}
                            inputId={p.id}
                          />
                          <label htmlFor={p.id} className="cursor-pointer text-sm">
                            {p.descripcion}
                            <span className="text-xs text-400 ml-2">{p.id}</span>
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Modal nuevo rol */}
      <Dialog
        header="Crear nuevo rol"
        visible={modalNuevoRol}
        style={{ width: "400px" }}
        onHide={() => setModalNuevoRol(false)}
        footer={
          <div>
            <Button label="Cancelar" text onClick={() => setModalNuevoRol(false)} />
            <Button label="Crear" icon="pi pi-check" loading={creando} onClick={crearRol} />
          </div>
        }
      >
        <div className="flex flex-column gap-3 pt-2">
          <div>
            <label className="block text-sm font-medium mb-1">ID del rol *</label>
            <InputText
              value={nuevoRol.id}
              onChange={(e) => setNuevoRol({ ...nuevoRol, id: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, "") })}
              placeholder="ej: supervisor"
              className="w-full"
            />
            <small className="text-400">Solo letras minúsculas, números y guiones</small>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Nombre *</label>
            <InputText
              value={nuevoRol.nombre}
              onChange={(e) => setNuevoRol({ ...nuevoRol, nombre: e.target.value })}
              placeholder="ej: Supervisor"
              className="w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Descripción</label>
            <InputText
              value={nuevoRol.descripcion}
              onChange={(e) => setNuevoRol({ ...nuevoRol, descripcion: e.target.value })}
              placeholder="Breve descripción del rol"
              className="w-full"
            />
          </div>
        </div>
      </Dialog>
    </div>
  );
}
